import React, { useState } from 'react';
import { QRCodeForm as QRFormData, QRStyleOptions } from '../types';
import { QRService } from '../services/qrService';
import { downloadQRCodePNG, downloadQRCodeSVG, generateQRCodeURL } from '../utils/qrGenerator';
import QRStyleEditor from './QRStyleEditor';
import QRCodeDisplay from './QRCodeDisplay';
import AdvancedQRCodeEditor from './AdvancedQRCodeEditor';

interface QRCodeFormProps {
  initialData?: QRFormData;
  onSubmit: (data: QRFormData) => void;
  onCancel?: () => void;
  isEditing?: boolean;
  isSubmitting?: boolean;
}

const createInitialData = (): QRFormData => ({
  id: QRService.generateQRId(),
  target: '',
  title: '',
  description: '',
  styleOptions: {
    size: 256,
    margin: 2,
    color: { dark: '#111827', light: '#FFFFFF' },
    errorCorrectionLevel: 'M',
    logo: { enabled: false, size: 15, backgroundColor: '#FFFFFF', borderRadius: 8 },
    style: { transparentBackground: false, dotStyle: 'square' }
  },
  status: 'active',
  favorite: false,
  tags: [],
  notes: '',
  fallbackTarget: '',
  utm: { enabled: false },
  schedule: { enabled: false, timezone: 'Europe/Berlin' },
  smartRules: [],
  protection: { enabled: false },
  landingPage: { enabled: false, template: 'links', leadFields: ['name', 'email'] },
  changeComment: ''
});

const QRCodeForm: React.FC<QRCodeFormProps> = ({ initialData, onSubmit, onCancel, isEditing = false, isSubmitting = false }) => {
  const [formData, setFormData] = useState<QRFormData>(() => initialData || createInitialData());
  const [errors, setErrors] = useState<Partial<Record<'id' | 'target', string>>>({});

  const validateForm = () => {
    const nextErrors: Partial<Record<'id' | 'target', string>> = {};
    if (!formData.id.trim()) nextErrors.id = 'Add a unique ID.';
    else if (!/^[a-zA-Z0-9_-]+$/.test(formData.id)) nextErrors.id = 'Use letters, numbers, hyphens or underscores only.';
    else if (formData.id.length < 3) nextErrors.id = 'Use at least 3 characters.';

    if (!formData.target.trim()) nextErrors.target = 'Add a destination URL.';
    else {
      try {
        const url = new URL(formData.target);
        if (!['http:', 'https:'].includes(url.protocol)) nextErrors.target = 'Use an http:// or https:// URL.';
      } catch {
        nextErrors.target = 'Enter a complete URL, including https://.';
      }
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (validateForm()) onSubmit(formData);
  };

  const updateField = (field: keyof QRFormData, value: string) => {
    setFormData((current) => ({ ...current, [field]: value }));
    if (field === 'id' || field === 'target') setErrors((current) => ({ ...current, [field]: undefined }));
  };

  const updateStyle = (styleOptions: QRStyleOptions) => setFormData((current) => ({ ...current, styleOptions }));
  const updateAdvanced = (updates: Partial<QRFormData>) => setFormData((current) => ({ ...current, ...updates }));

  const regenerateId = () => {
    updateField('id', QRService.generateQRId());
  };

  return (
    <form onSubmit={handleSubmit} className="qr-builder-grid">
      <div className="min-w-0 space-y-5">
        <section className="app-panel overflow-hidden">
          <div className="border-b border-slate-200 px-5 py-5 sm:px-6">
            <div className="flex items-start gap-3">
              <span className="builder-step">1</span>
              <div><h2 className="text-base font-semibold text-slate-950">Destination</h2><p className="mt-1 text-sm text-slate-500">Define the permanent ID and the URL it should open.</p></div>
            </div>
          </div>

          <div className="space-y-5 p-5 sm:p-6">
            <div>
              <div className="flex items-center justify-between gap-3"><label htmlFor="id" className="app-label">QR code ID</label><span className="text-xs text-slate-400">Permanent after creation</span></div>
              <div className="mt-2 flex gap-2">
                <div className={`app-input-group flex-1 ${errors.id ? 'is-error' : ''}`}>
                  <span className="hidden border-r border-slate-200 pr-3 font-mono text-sm text-slate-400 sm:inline">/qr/</span>
                  <input id="id" type="text" value={formData.id} onChange={(event) => updateField('id', event.target.value)} disabled={isEditing || isSubmitting} className="min-w-0 flex-1 bg-transparent font-mono text-sm text-slate-900 outline-none disabled:text-slate-500" aria-invalid={Boolean(errors.id)} />
                </div>
                {!isEditing && <button type="button" onClick={regenerateId} disabled={isSubmitting} className="app-secondary-button flex-shrink-0">Generate</button>}
              </div>
              {errors.id && <FieldError>{errors.id}</FieldError>}
            </div>

            <div>
              <label htmlFor="target" className="app-label">Destination URL</label>
              <div className={`app-input-group mt-2 ${errors.target ? 'is-error' : ''}`}>
                <svg className="h-4 w-4 flex-shrink-0 text-slate-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10 13a5 5 0 0 0 7.5.5l2-2a5 5 0 0 0-7-7l-1.15 1.15M14 11a5 5 0 0 0-7.5-.5l-2 2a5 5 0 0 0 7 7l1.15-1.15"/></svg>
                <input id="target" type="url" value={formData.target} onChange={(event) => updateField('target', event.target.value)} disabled={isSubmitting} placeholder="https://example.com/campaign" className="min-w-0 flex-1 bg-transparent text-sm text-slate-900 outline-none placeholder:text-slate-400" aria-invalid={Boolean(errors.target)} />
              </div>
              {errors.target ? <FieldError>{errors.target}</FieldError> : <p className="mt-2 text-xs leading-5 text-slate-500">You can change this destination later without replacing the printed QR code.</p>}
            </div>

            <div className="grid gap-5 sm:grid-cols-2">
              <div><label htmlFor="title" className="app-label">Internal title <span className="font-normal text-slate-400">(optional)</span></label><input id="title" type="text" value={formData.title} onChange={(event) => updateField('title', event.target.value)} disabled={isSubmitting} placeholder="Summer campaign" className="app-input mt-2" /></div>
              <div><label htmlFor="description" className="app-label">Description <span className="font-normal text-slate-400">(optional)</span></label><input id="description" type="text" value={formData.description} onChange={(event) => updateField('description', event.target.value)} disabled={isSubmitting} placeholder="Where this code is being used" className="app-input mt-2" /></div>
            </div>
          </div>
        </section>

        <section className="app-panel overflow-hidden">
          <div className="border-b border-slate-200 px-5 py-5 sm:px-6">
            <div className="flex items-start gap-3">
              <span className="builder-step">2</span>
              <div><h2 className="text-base font-semibold text-slate-950">Appearance</h2><p className="mt-1 text-sm text-slate-500">Everything visual stays in one place and updates the preview instantly.</p></div>
            </div>
          </div>
          <QRStyleEditor options={formData.styleOptions || {}} onChange={updateStyle} />
        </section>

        <AdvancedQRCodeEditor value={formData} onChange={updateAdvanced} disabled={isSubmitting} />

        <div className="flex flex-col-reverse gap-3 rounded-2xl border border-slate-200 bg-white p-4 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-xs leading-5 text-slate-500">Your dynamic link remains the same when the destination changes.</p>
          <div className="flex gap-3">
            {onCancel && <button type="button" onClick={onCancel} disabled={isSubmitting} className="app-secondary-button flex-1 justify-center sm:flex-none">Cancel</button>}
            <button type="submit" disabled={isSubmitting} className="app-primary-button flex-1 justify-center sm:flex-none">
              {isSubmitting && <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" />}
              {isEditing ? 'Save changes' : 'Create QR code'}
            </button>
          </div>
        </div>
      </div>

      <aside className="min-w-0 xl:sticky xl:top-[104px] xl:h-fit">
        <div className="app-panel overflow-hidden">
          <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4"><div><h2 className="text-sm font-semibold text-slate-950">Live preview</h2><p className="mt-0.5 text-xs text-slate-500">Updates automatically</p></div><span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-700"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500"/>Ready</span></div>
          <div className="qr-preview-stage">
            <div className="qr-preview-card">
              <QRCodeDisplay qrId={formData.id || 'preview'} size={236} showDownloadButton={false} styleOptions={formData.styleOptions} />
            </div>
          </div>
          <div className="space-y-4 border-t border-slate-200 p-5">
            <div><p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">Dynamic URL</p><p className="mt-1 truncate font-mono text-xs text-slate-700" title={generateQRCodeURL(formData.id || 'preview')}>{generateQRCodeURL(formData.id || 'preview')}</p></div>
            <div><p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">Destination</p><p className="mt-1 truncate text-sm text-slate-700">{formData.target || 'Add a destination URL'}</p></div>
            <div className="rounded-xl bg-blue-50 p-3 text-xs leading-5 text-blue-800"><strong>Print-safe:</strong> future destination changes do not alter this QR image.</div>
            <div className="grid grid-cols-2 gap-2">
              <button type="button" onClick={() => void downloadQRCodePNG(formData.id || 'preview', `${formData.title || formData.id || 'qr-code'}.png`, formData.styleOptions)} className="app-secondary-button justify-center">Download PNG</button>
              <button type="button" onClick={() => void downloadQRCodeSVG(formData.id || 'preview', `${formData.title || formData.id || 'qr-code'}.svg`, formData.styleOptions)} className="app-secondary-button justify-center">Download SVG</button>
            </div>
          </div>
        </div>
      </aside>
    </form>
  );
};

const FieldError: React.FC<{ children: React.ReactNode }> = ({ children }) => <p className="mt-2 flex items-center gap-1.5 text-xs font-medium text-red-600"><span className="grid h-4 w-4 place-items-center rounded-full bg-red-100 text-[10px]">!</span>{children}</p>;

export default QRCodeForm;
